import { FormEvent, memo, MouseEvent, useEffect, useState } from 'react';
import { Box, Checkbox, Menu, MenuButton, MenuItem, MenuList } from '@chakra-ui/react';
import { Button, Colors, Icon, Text } from '@mercator-gcp/grout';
import { capitalize, isEmpty } from 'lodash';
import OverlaySpinner from 'components/elements/Loading/OverlaySpinner';
import { PolygonMenuIcon } from 'components/icons/PolygonMenuIcon';
import { FlexRow } from 'components/layout/FlexRow';
import { FilterToolbarStyles as styles } from 'components/widgets/FilterToolbar/style';
import { useGetPolygonsByZone } from 'hooks/projectMap/useParcelPolygons';
import { useMapBounds } from 'hooks/useMapBounds';
import { boundsStringToLiteral } from 'pages/ProjectInsights/services';
import {
  usePolygonOverlayNamesByRegion,
  ZoneNamesAndCity,
} from 'services/API/BackendAPI';
import { useCurrentCity } from 'state/currentLocation';
import { useCurrentRegion } from 'state/currentRegion';
import { useMapState, useZoneOverlayPolygons } from 'state/map';
import { useMapBoundsAsCardinalDirections } from 'state/mapSettings';
import { cityMap } from 'utils/misc';

const polygonColorPaleteTileStyle = (color: string) => ({
  background: color,
  width: '12px',
  height: '12px',
  borderRadius: '4px',
  ml: 'padding_8',
  mr: 'padding_8',
});

const titleStyle = {
  color: Colors.ArmyGreen500,
  padding: 'padding_8',
  paddingTop: 'padding_12',
};

const menuWrapperStyle = { padding: 0 };

const zoneMenuStyle = {
  maxHeight: '300px',
  overflowY: 'auto',
  borderTop: '1px solid #E8E9E8',
  borderBottom: '1px solid #E8E9E8',
  '>button:last-of-type': {
    borderBottom: 'none',
  },
};

const zoneItemStyle = {
  borderBottom: '1px solid #E8E9E8',
  padding: 'padding_8',
  textTransform: 'uppercase',
};

const buttonContainerStyle = {
  width: '100%',
  justifyContent: 'space-between',
  padding: 'padding_8',
};

const POLYGON_COLORS = [
  '#F75C3B',
  '#304CDB',
  '#D7BF32',
  '#8AC4D0',
  '#B66A33',
  '#FE8F28',
  '#468658',
  '#A394FF',
  '#CDAD55',
  '#309DDB',
  '#FF2F7A',
  '#595E8A',
  '#E4C5D4',
  '#7FF09F',
  '#FCE4A7',
  '#FC89C0',
  '#D2CFD5',
  '#468712',
  '#B292CF',
];

const PolygonFilterComponent = () => {
  const [menuOpen, setMenuOpen] = useState<boolean>(false);
  const [selectedPolygonOverlayList, setSelectedPolygonOverlayList] = useState<
    Record<string, string>
  >({});
  const { getDefaultBoundingBox } = useMapBounds();
  const [mapBoundsAsCardinalDirections] = useMapBoundsAsCardinalDirections();
  const [lastSelectedZone, setLastSelectedZone] = useState<string>('');
  const { currentCity } = useCurrentCity();
  const [zoneOverlayPolygons, setZoneOverlayPolygons] = useZoneOverlayPolygons();
  const {
    selectedPolygons,
    updateSelectedPolygons,
    updateAvailablePolygons,
    availablePolygons,
  } = useMapState();
  const { currentRegion } = useCurrentRegion();
  const { data: zoneNames, isLoading: zoneNamesIsLoading } =
    usePolygonOverlayNamesByRegion(currentRegion || '');
  const [availableZoneDetails, setAvailableZoneDetails] = useState<ZoneNamesAndCity[]>(
    [],
  );

  const response = useGetPolygonsByZone(
    mapBoundsAsCardinalDirections?.north
      ? mapBoundsAsCardinalDirections
      : boundsStringToLiteral(getDefaultBoundingBox(currentCity)),
    cityMap[currentCity],
    lastSelectedZone,
  );

  useEffect(() => {
    // set local selection to state selection to keep states aligned
    setSelectedPolygonOverlayList(selectedPolygons);
  }, []);

  const handleClick = (
    e: MouseEvent<HTMLButtonElement> | FormEvent<HTMLButtonElement>,
    overlayColor: string,
  ) => {
    if (response.isLoading) {
      return;
    }
    const { value } = e.currentTarget;

    if (selectedPolygonOverlayList[value]) {
      const newSelectedPolygons = { ...selectedPolygonOverlayList };
      delete newSelectedPolygons[value];
      setSelectedPolygonOverlayList(newSelectedPolygons);
      updateSelectedPolygons(newSelectedPolygons);
    } else {
      const newSelectedPolygons = {
        ...selectedPolygonOverlayList,
        [value]: overlayColor,
      };
      setSelectedPolygonOverlayList(newSelectedPolygons);
      if (zoneOverlayPolygons[value]) {
        // if we already have polygons in state, load them immediately.
        updateSelectedPolygons(newSelectedPolygons);
      }
      setLastSelectedZone(value);
    }
  };

  useEffect(() => {
    if (!response.isLoading) {
      if (response.data && !isEmpty(response.data)) {
        setZoneOverlayPolygons({
          ...zoneOverlayPolygons,
          ...response?.data?.data,
        });
        updateSelectedPolygons(selectedPolygonOverlayList);
      }
    }
  }, [response.isLoading, response.data]);

  useEffect(() => {
    if (zoneNamesIsLoading) {
      // API call is still loading
      return;
    }

    if (!zoneNames) {
      setAvailableZoneDetails([]);
      updateAvailablePolygons([]);

      return;
    }

    const uniqueZonesByName = Array.from(
      new Map(zoneNames.map((item) => [item.zone_overlay_name, item])).values(),
    ).sort((a, b) => a.zone_overlay_name.localeCompare(b.zone_overlay_name));
    const onlyZoneNames = uniqueZonesByName.map((item) => item.zone_overlay_name);

    setAvailableZoneDetails(uniqueZonesByName);
    updateAvailablePolygons(onlyZoneNames);
  }, [zoneNamesIsLoading, zoneNames, setAvailableZoneDetails, updateAvailablePolygons]);

  const clearAllSelections = () => {
    updateSelectedPolygons({});
    setSelectedPolygonOverlayList({});
  };

  if (!availablePolygons.length) {
    return null;
  }

  return (
    <FlexRow>
      <Menu closeOnSelect={false} isOpen={menuOpen}>
        <MenuButton onClick={() => setMenuOpen(!menuOpen)}>
          <PolygonMenuIcon />
        </MenuButton>
        <MenuList sx={menuWrapperStyle}>
          <Text.SmallLabel as="div" sx={titleStyle}>
            Zone Overlay
          </Text.SmallLabel>
          <Box sx={zoneMenuStyle}>
            {response.isLoading && <OverlaySpinner sx={{ maxHeight: '300px' }} />}
            {availableZoneDetails.map((zone, i) => {
              const polygonColor = POLYGON_COLORS[i % POLYGON_COLORS.length];

              return (
                <MenuItem
                  key={`zone-overlay-option-${zone.zone_overlay_name.replace(
                    /\s+/g,
                    '-',
                  )}-${zone.city_id}`}
                  sx={zoneItemStyle}
                  value={zone.zone_overlay_name}
                  onChange={(e) => {
                    handleClick(e, polygonColor);
                  }}
                  onClick={(e) => {
                    handleClick(e, polygonColor);
                  }}
                >
                  <Checkbox
                    isChecked={!!selectedPolygonOverlayList[zone.zone_overlay_name]}
                    sx={styles.checkBoxStyles}
                  />
                  <Box sx={polygonColorPaleteTileStyle(polygonColor)} />
                  <Text.Small sx={{ fontWeight: '600', color: Colors.TextQuiet }}>
                    {capitalize(zone.zone_overlay_name)}
                  </Text.Small>
                </MenuItem>
              );
            })}
          </Box>
          <FlexRow sx={buttonContainerStyle}>
            <Button
              variant="functional"
              onClick={() => {
                setMenuOpen(false);
              }}
            >
              Close
            </Button>
            <Button
              variant="unstyled"
              startAdornment={<Icon name={Icon.Name.CloseX} />}
              onClick={clearAllSelections}
            >
              Clear all
            </Button>
          </FlexRow>
        </MenuList>
      </Menu>
    </FlexRow>
  );
};

export default memo(PolygonFilterComponent);
